<template>
  <liste-template>
    <template #header>
      <h1 class="title is-size-3">{{ $t('listeFormatAffiche.title') }}</h1>
      <breadcrumb :breadcrumb="breadcrumb"/>
    </template>
    <template #action>
      <button
        class="button is-addOrEdit"
        @click="checkPermissions(perm.maj)
          ? $router.push({ name: 'm.bodac.format_affiche' })
          : showPopUp()"
        :class="{ 'button-access-no-permission' : !checkPermissions(perm.maj) }">
        {{ $t('listeFormatAffiche.actions.add')}}
      </button>
    </template>
    <template #bar-left>
      <form @submit.prevent="recherche()" class="column is-narrow">
        <b-input
          v-model="critereRecherche.auto"
          :placeholder="$t('general.button.search')"
          type="search"
          icon="magnify">
        </b-input>
      </form>
      <div class='column is-narrow'>
        <button class="button" @click='setShowFiltre'>
          <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
          <span class="is-size-7">
            {{$t('general.button.filtre')}}
            <label class="has-text-weight-bold"
                    :class="nombreCritere > 0 ? '' : 'is-invisible'">
              ({{ nombreCritere }})
            </label>
          </span>
        </button>
        <span @click="reinitialiserFiltre()"
              class="info-circle-right is-pulled-right is-cursor-pointer">
          <b-icon icon="close-circle" type="is-danger"></b-icon>
        </span>
      </div>
    </template>
    <template #bar-right>
      <parametrage-table
        :reference-table="referenceTable"
        @endLoadParamtre="startArrayRendering = true">
      </parametrage-table>
    </template>
    <template #content>
      <v-server-table
        v-if="startArrayRendering" url=""
        :columns="columns"
        :options="options"
        :ref="referenceTable"
        @loaded="finLoading"
        @loading="debutLoading">
        <template #code="{ row }">
          <a class="lien"
            :class="{ 'text-access-no-permission' : !checkPermissions(perm.lecture) }"
            @click="checkPermissions(perm.lecture)
              ? $router.push({
                name: 'c.bodac.format_affiche',
                params: { id: row.id },
              }) : showPopUp()">
            {{row.code}}
          </a>
        </template>
        <template #natureSupportAffiche="{ row }">
          {{ getNatureSupportAffiche(row.natureSupportAfficheId) }}
        </template>
        <template #typeMateriel="{ row }">
          {{ getTypeMateriel(row.typeMaterielId) }}
        </template>
        <template #edit="{ row }">
          <div class="has-text-centered">
            <btn-action-icon
              :permission="perm.maj"
              @is-click="$router.push({
                name: 'm.bodac.format_affiche',
                params: { id: row.id },
              })"
              icon-type="is-success"
              icon="pencil-box-outline">
            </btn-action-icon>
          </div>
        </template>
        <template #archiver>
          <div class="has-text-centered">
            <btn-action-icon
              :permission="perm.supprimer"
              @is-click="inProgress()"
              icon-type="is-default"
              icon="archive">
            </btn-action-icon>
          </div>
        </template>
        <template #delete>
          <div class="has-text-centered">
            <btn-action-icon
              :permission="perm.supprimer"
              @is-click="inProgress()"
              icon-type="is-danger"
              icon="delete-forever">
            </btn-action-icon>
          </div>
        </template>
        <template #afterTable>
          <table-items-in-view
            :perPage="resultPerPage"
            @input="$refs[referenceTable].setLimit($event)"/>
        </template>
      </v-server-table>
    </template>
    <template #filter>
      <div class="componentBrief" v-show="showFiltre">
        <filtre-format-affiche
            ref="filtreListeFormatAffiche"
            @toggleFiltre="setShowFiltre"
            @recherche="recherche"/>
      </div>
    </template>
  </liste-template>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import Breadcrumb from '@/components/general/breadcrumb';
import BtnActionIcon from '@/components/general/btnActionIcon';
import ParametrageTable from '@/components/general/parametrageTable';
import TableItemsInView from '@/components/general/TableItemsInView';
import ListeTemplate from '@/components/general/template/listeTemplate';
import inProgress from '@/services/general/inProgress';
import messageErreur from '@/services/general/messageErreur';
import subscribeMutation from '@/services/store/subscribeMutation';
import {
  TEXTS,
  SORT_ICON,
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
} from '@/configuration/clientTableConf';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import { FORMAT_AFFICHE } from '@/configuration/permissionsBodacConf';
import { REFERENCE_TABLE_FORMAT_AFFICHE } from '@/configuration/general/referenceTableConf';
import { SET_DETECT_CHANGE_SOCIETE } from '@/store/modules/societe-mutation-type';
import filtreFormatAffiche from '@/components/parametrage/formatAffiche/filtreFormatAffiche';
import calculNombreCritere from '@/services/general/calculNombreCritere';

const Societe = createNamespacedHelpers('societe');
const Parametres = createNamespacedHelpers('parametres');
const Permissions = createNamespacedHelpers('permissions');
const FormatAffiche = createNamespacedHelpers('formatAffiche');

export default {
  name: 'LFormatAffiche',
  data() {
    return {
      perm: FORMAT_AFFICHE,
      unsubscribeValue: null,
      startArrayRendering: false,
      resultPerPage: RESULTS_PER_PAGE,
      referenceTable: REFERENCE_TABLE_FORMAT_AFFICHE,
      showFiltre: false,
    };
  },
  components: {
    Breadcrumb,
    ListeTemplate,
    BtnActionIcon,
    ParametrageTable,
    TableItemsInView,
    filtreFormatAffiche,
  },
  computed: {
    ...FormatAffiche.mapGetters({
      items: 'items',
      totalCount: 'totalCount',
      critereRecherche: 'criteresRecherche',
    }),
    ...Permissions.mapGetters({
      checkPermissions: 'checkPermissions',
    }),
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    nombreCritere() {
      return calculNombreCritere(this.critereRecherche, ['auto']);
    },
    breadcrumb() {
      const output = [
        { title: this.$i18n.t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('parametrage.title'), route: { name: 'l.parametrage.general' }, isActive: false },
        { title: this.$i18n.t('listeFormatAffiche.breadcrumb.liste'), route: '', isActive: true },
      ];
      return output;
    },
    columnsData() {
      return [
        { name: 'code', sortable: true },
        { name: 'libelle', sortable: true },
        { name: 'natureSupportAffiche', sortable: true },
        { name: 'typeMateriel', sortable: true },
        { name: 'largeur', sortable: true },
        { name: 'longueur', sortable: true },
        { name: 'edit', noTranslate: true },
        { name: 'archiver', noTranslate: true },
        { name: 'delete', noTranslate: true },
      ];
    },
    columns() {
      return this.columnsData.map((column) => column.name);
    },
    options() {
      const headings = this.columnsData.reduce((acc, val) => {
        acc[val.name] = !val.noTranslate ? this.$t(`listeFormatAffiche.tab.${val.name}`) : '';
        return acc;
      }, {});
      return {
        headings,
        sortIcon: SORT_ICON,
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        requestFunction: ((data) => {
          data.query = cloneDeep(this.critereRecherche);
          data.query.societeId = this.societeActiveId;
          return this.actionLoad(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.items,
            count: this.totalCount,
          };
        }),
        columnsClasses: {
          edit: 'colonneSelected',
          delete: 'colonneSelected',
          archiver: 'colonneSelected',
        },
        sortable: this.columnsData.filter((c) => c.sortable).map((c) => c.name),
      };
    },
    natureSupportAffiche() {
      return this.parametre(PARAMETRES_KEYS.natureSupportAffiche);
    },
    typeMateriel() {
      return this.parametre(PARAMETRES_KEYS.materielType);
    },
  },
  methods: {
    ...FormatAffiche.mapActions({
      actionLoad: 'loadItems',
    }),
    ...Permissions.mapActions({
      showPopUp: 'showPopup',
    }),
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    finLoading() {
      this.loadingComponent.close();
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    recherche() {
      this.$refs[this.referenceTable].refresh();
    },
    inProgress() {
      inProgress(this.$i18n);
    },
    goFirstPage() {
      this.$refs[this.referenceTable].setPage(1);
    },
    getNatureSupportAffiche(id) {
      return this.natureSupportAffiche.find((n) => n.id === id)?.libelle || '';
    },
    getTypeMateriel(id) {
      return this.typeMateriel.find((t) => t.id === id)?.libelle || '';
    },
    setShowFiltre() {
      this.showFiltre = !this.showFiltre;
    },
    reinitialiserFiltre() {
      this.$refs.filtreListeFormatAffiche.viderCritere();
      this.recherche();
    },
  },
  beforeDestroy() {
    this.unsubscribeValue();
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.recherche, this.goFirstPage);
      this.setInformationPage(this.$t('pageParametrage.formatAffiche.liste'));
      await this.loadParametres([
        PARAMETRES_KEYS.materielType,
        PARAMETRES_KEYS.natureSupportAffiche,
      ]);
    } catch (e) {
      messageErreur(this.$i18n);
      throw (e);
    } finally {
      loadingComponent.close();
    }
  },
};
</script>
